import {
  TeamOutlined,
  LineChartOutlined,
  SettingOutlined,
  UserOutlined,
  CalendarOutlined,
  DashboardOutlined,
  AuditOutlined,
  ScheduleOutlined,
  AreaChartOutlined,
  FileDoneOutlined,
  MailOutlined,
  PhoneOutlined,
  CameraOutlined,
  HomeOutlined,
  ShopOutlined,
  AlertOutlined,
  CarOutlined,
  ClockCircleOutlined,
  BarsOutlined,
  SolutionOutlined,
  FileTextOutlined,
  FolderOutlined,
  ForkOutlined,
  TagsOutlined,
  ToTopOutlined,
  QuestionOutlined,
  TagOutlined,
  RobotOutlined,
  ApiOutlined,
  MessageOutlined,
} from "@ant-design/icons";

export interface DashboardRouteConfig {
  key: string;
  label: string;
  icon: React.ReactNode;
  match: (pathname: string) => boolean;
  requiresRole?: string;
  requiresPermissions?: string[];
  requiresFeatures?: string[];
  children?: DashboardRouteConfig[];
}

export const dashboardNavItems: DashboardRouteConfig[] = [
  {
    key: "dashboard",
    label: "Dashboard",
    icon: <DashboardOutlined />,
    match: (p) => p === "/dashboard",
    requiresPermissions: ["admin_dashboard_nav"],
  },
  {
    key: "chat",
    label: "Berichten",
    icon: <MessageOutlined />,
    match: (p) => p.includes("chat"),
    requiresPermissions: ["admin_chat_conversation_read"],
  },
  {
    key: "dossiers",
    label: "Dossiers",
    icon: <TeamOutlined />,
    match: (p) =>
      p.includes("dossiers") &&
      !p.includes("dossiers/concepts") &&
      !p.includes("dossiers/accident-reports"),
    requiresPermissions: ["admin_dossier_nav"],
  },
  {
    key: "kanban",
    label: "Statusbord",
    icon: <CalendarOutlined />,
    match: (p) => p.includes("kanban"),
    requiresPermissions: ["admin_kanban_nav"],
  },
  {
    key: "analytics",
    label: "Management Informatie",
    icon: <LineChartOutlined />,
    match: (p) => p.includes("analytics"),
    requiresPermissions: ["admin_analytics_nav"],
  },
  {
    key: "users",
    label: "Gebruikers",
    icon: <UserOutlined />,
    match: (p) => p.includes("users"),
    requiresPermissions: ["admin_user_nav"],
  },
  {
    key: "service-partners",
    label: "Herstellers",
    icon: <ShopOutlined />,
    match: (p) =>
      p.includes("service-partners") &&
      !p.includes("service-partners/") &&
      !p.includes("/base/service-partners"),
    requiresPermissions: ["admin_service_partner_nav"],
    children: [
      {
        key: "service-partners/partners",
        label: "Herstellers",
        icon: <ShopOutlined />,
        match: (p) => p.includes("service-partners/partners"),
        requiresPermissions: ["admin_service_partner_nav"],
      },
      {
        key: "service-partners/collections",
        label: "Subnetwerken",
        icon: <ShopOutlined />,
        match: (p) => p.includes("service-partners/collections"),
        requiresPermissions: ["admin_service_partner_collection_nav"],
      },
    ],
  },
  {
    key: "appointment-time-slots/plans",
    label: "Capaciteitsplanning",
    icon: <ScheduleOutlined />,
    match: (p) => p.includes("appointment-time-slots/plans"),
    requiresPermissions: ["admin_appointment_time_slot_plan_nav"],
  },
  {
    key: "dealer-intake",
    label: "Dealer Intake",
    icon: <CarOutlined />,
    match: (p) =>
      p.includes("dealer-intake") && !p.includes("dealer-intake-v2"),
    requiresPermissions: ["admin_dealer_intake_nav"],
    requiresFeatures: ["dealer_intake"],
  },
  {
    key: "dealer-intake-v2",
    label: "Dealer Intake V2",
    icon: <CarOutlined />,
    match: (p) => p.includes("dealer-intake-v2"),
    requiresPermissions: ["admin_dealer_intake_v2_nav"],
    requiresFeatures: ["dealer_intake"],
  },
  {
    key: "dossiers/concepts",
    label: "Concepten",
    icon: <FileTextOutlined />,
    match: (p) => p.includes("dossiers/concepts"),
    requiresPermissions: ["admin_concept_nav"],
  },
  {
    key: "dossiers/accident-reports",
    label: "Schadeformulieren",
    icon: <FileDoneOutlined />,
    match: (p) => p.includes("dossiers/accident-reports"),
    requiresPermissions: ["admin_accident_report_placeholder_import_nav"],
  },
  {
    key: "faqs",
    label: "Vraag & Antwoord",
    icon: <QuestionOutlined />,
    match: (p) => p.includes("faqs"),
    requiresPermissions: ["admin_faq_nav"],
  },
  {
    key: "knowledge-base",
    label: "Kennisbank",
    icon: <QuestionOutlined />,
    match: (p) => p.includes("knowledge-base"),
    requiresPermissions: ["admin_knowledge_base_nav"],
  },
  {
    key: "replacement-transport",
    label: "Vervangend vervoer",
    icon: <ClockCircleOutlined />,
    match: (p) => p.includes("replacementtransport"),
    requiresPermissions: ["admin_replacement_transport_plan_nav"],
    children: [
      {
        key: "replacement-transport/options",
        label: "Opties",
        icon: <BarsOutlined />,
        match: (p) => p.includes("replacement-transport/options"),
        requiresPermissions: ["admin_replacement_transport_plan_nav"],
      },
      {
        key: "replacement-transport/plans",
        label: "Opdrachtgevers",
        icon: <SolutionOutlined />,
        match: (p) => p.includes("replacement-transport/plans"),
        requiresPermissions: ["admin_replacement_transport_plan_nav"],
      },
    ],
  },
  {
    key: "benchmark",
    label: "AI Benchmark Cockpit",
    icon: <DashboardOutlined />,
    match: (p) => p.includes("benchmark"),
    requiresRole: "ROLE_OIDC_ADMIN",
  },
  {
    key: "base",
    label: "Stamgegevens",
    icon: <SettingOutlined />,
    match: (p) => p.includes("/base/"),
    requiresRole: "ROLE_OIDC_ADMIN",
    children: [
      {
        key: "base/service-partners",
        label: "Stamgegevens herstellers",
        icon: <SettingOutlined />,
        match: (p) => p.includes("base/service-partners"),
        requiresRole: "ROLE_OIDC_ADMIN",
      },
    ],
  },
  {
    key: "admin_management",
    label: "Configuratie",
    icon: <SettingOutlined />,
    match: (p) => p.includes("admin_management"),
    requiresRole: "ROLE_OIDC_ADMIN",
    children: [
      {
        key: "agents",
        label: "Agents",
        icon: <RobotOutlined />,
        match: (p) => p.includes("agents"),
        requiresRole: "ROLE_OIDC_ADMIN",
      },
      {
        key: "ai-profiles",
        label: "AI Provider & Model profiles",
        icon: <ApiOutlined />,
        match: (p) => p.includes("ai-profiles"),
        requiresRole: "ROLE_OIDC_ADMIN",
      },
      {
        key: "affiliates",
        label: "Affiliates",
        icon: <SettingOutlined />,
        match: (p) => p.includes("affiliates"),
        requiresRole: "ROLE_OIDC_ADMIN",
      },
      {
        key: "companies",
        label: "Bedrijven",
        icon: <HomeOutlined />,
        match: (p) => p.includes("companies"),
        requiresPermissions: ["admin_company_read"],
      },
      {
        key: "document-libraries",
        label: "Documenten Bibliotheek",
        icon: <FolderOutlined />,
        match: (p) => p.includes("document-libraries"),
        requiresPermissions: ["admin_document_library_nav"],
      },
      {
        key: "email-templates",
        label: "E-mail templates",
        icon: <MailOutlined />,
        match: (p) =>
          p.includes("email-templates") &&
          !p.includes("email-templates/layouts"),
        requiresPermissions: ["admin_email_template_read"],
      },
      {
        key: "email-templates/layouts",
        label: "E-mail template layouts",
        icon: <MailOutlined />,
        match: (p) => p.includes("email-templates/layouts"),
        requiresPermissions: ["admin_email_template_layout_read"],
      },
      {
        key: "image-captures",
        label: "ImageCaptures",
        icon: <CameraOutlined />,
        match: (p) =>
          p.includes("image-captures") &&
          !p.includes("image-captures/accounts"),
        requiresPermissions: ["admin_image_capture_read"],
      },
      {
        key: "image-captures/accounts",
        label: "ImageCapture accounts",
        icon: <UserOutlined />,
        match: (p) => p.includes("image-captures/accounts"),
        requiresPermissions: ["admin_image_capture_account_read"],
      },
      {
        key: "sms-templates",
        label: "SMS Templates",
        icon: <PhoneOutlined />,
        match: (p) => p.includes("sms-templates"),
        requiresPermissions: ["admin_sms_template_read"],
      },
      {
        key: "roles",
        label: "Rollen",
        icon: <TagsOutlined />,
        match: (p) => p.includes("roles") && !p.includes("roles/triggers"),
        requiresPermissions: ["admin_roles_read"],
      },
      {
        key: "document-templates",
        label: "Document templates",
        icon: <FileTextOutlined />,
        match: (p) => p.includes("document-templates"),
        requiresPermissions: ["admin_document_template_read"],
      },
      {
        key: "external-webhooks",
        label: "Externe webhooks",
        icon: <ToTopOutlined />,
        match: (p) => p.includes("external-webhooks"),
        requiresPermissions: ["admin_external_webhook_read"],
      },
      {
        key: "rules",
        label: "Rules",
        icon: <AlertOutlined />,
        match: (p) => p.includes("rules"),
        requiresPermissions: ["admin_rules_read"],
      },
      {
        key: "evaluation-groups",
        label: "Evaluatiegroepen",
        icon: <AuditOutlined />,
        match: (p) => p.includes("evaluation-groups"),
        requiresPermissions: ["admin_evaluation_engine_read"],
      },
      {
        key: "form-configurations",
        label: "Formulieren",
        icon: <FileDoneOutlined />,
        match: (p) => p.includes("form-configurations"),
        requiresPermissions: ["admin_form_configuration_read"],
      },
      {
        key: "service-partners/tags",
        label: "Service partner tags",
        icon: <TagOutlined />,
        match: (p) => p.includes("service-partners/tags"),
        requiresPermissions: ["admin_service_partner_tag_nav"],
      },
    ],
  },
];

export const claimNavItems: DashboardRouteConfig[] = [
  {
    key: "chat",
    label: "Berichten",
    icon: <MessageOutlined />,
    match: (p) => p.includes("chat"),
    requiresPermissions: ["admin_chat_conversation_read"],
  },
  {
    key: "claims/dashboard",
    label: "Dashboard",
    icon: <DashboardOutlined />,
    match: (p) => p.includes("dashboard"),
  },
  {
    key: "claims/tasks",
    label: "Taken",
    icon: <ScheduleOutlined />,
    match: (p) => p.includes("tasks"),
  },
  {
    key: "claims/dossiers",
    label: "Dossiers",
    icon: <FileDoneOutlined />,
    match: (p) => p.includes("dossiers"),
  },
  {
    key: "claims/kanban",
    label: "Status Planbord",
    icon: <CalendarOutlined />,
    match: (p) => p.includes("kanban"),
    children: [
      {
        key: "claims/kanban/calculaties",
        label: "Calculaties",
        icon: <CalendarOutlined />,
        match: (p) => p.includes("kanban/calculaties"),
      },
      {
        key: "claims/kanban/claims",
        label: "Claims",
        icon: <CalendarOutlined />,
        match: (p) => p.includes("kanban/claims"),
      },
      {
        key: "claims/kanban/herstel",
        label: "Herstel",
        icon: <CalendarOutlined />,
        match: (p) => p.includes("kanban/herstel"),
      },
    ],
  },
  {
    key: "claims/recourses",
    label: "Regres",
    icon: <AuditOutlined />,
    match: (p) => p.includes("recourses"),
  },
  {
    key: "claims/ranking",
    label: "Sturing",
    icon: <ForkOutlined />,
    match: (p) => p.includes("ranking"),
  },
  {
    key: "appointment-time-slots/plans",
    label: "Capaciteitsplanning",
    icon: <ScheduleOutlined />,
    match: (p) => p.includes("appointment-time-slots"),
  },
  {
    key: "claims/insights",
    label: "Inzichten",
    icon: <AreaChartOutlined />,
    match: (p) => p.includes("insights"),
  },
];
